import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Sparkles, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export type ActionType = "send_email" | "create_task" | "send_notification" | "webhook";

export const ACTION_LABELS: Record<ActionType, string> = {
  send_email: "Send Email",
  create_task: "Create Task",
  send_notification: "Send Notification",
  webhook: "Call Webhook",
};

interface Props {
  actionType: ActionType;
  config: Record<string, any>;
  onChange: (next: Record<string, any>) => void;
  stageName?: string;
}

export function AutomationConfigForm({ actionType, config, onChange, stageName }: Props) {
  const set = (key: string, value: any) => onChange({ ...config, [key]: value });
  const [aiOpen, setAiOpen] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiLoading, setAiLoading] = useState(false);

  const composeWithBridgette = async () => {
    if (!aiPrompt.trim()) {
      toast.error("Tell Bridgette what to write");
      return;
    }
    setAiLoading(true);
    try {
      const recipient =
        config.to_field === "sales_rep" ? "Sales rep" :
        config.to_field === "custom" ? (config.custom_email || "Recipient") :
        "Customer";
      const { data, error } = await supabase.functions.invoke("bridgette-compose-automation-email", {
        body: { stageName, recipient, prompt: aiPrompt },
      });
      if (error) throw error;
      if (data?.subject || data?.body) {
        onChange({ ...config, subject: data.subject || config.subject, body: data.body || config.body });
        toast.success("Bridgette drafted your email");
        setAiOpen(false);
        setAiPrompt("");
      } else {
        toast.error("No draft returned");
      }
    } catch (e: any) {
      toast.error(e?.message || "Bridgette could not compose");
    } finally {
      setAiLoading(false);
    }
  };

  if (actionType === "send_email") {
    return (
      <div className="space-y-3">
        <div>
          <Label>Send to</Label>
          <Select value={config.to_field || "customer"} onValueChange={(v) => set("to_field", v)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="customer">Customer (job contact)</SelectItem>
              <SelectItem value="sales_rep">Assigned sales rep</SelectItem>
              <SelectItem value="custom">Custom address</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {config.to_field === "custom" && (
          <div>
            <Label>Email address</Label>
            <Input value={config.custom_email || ""} onChange={(e) => set("custom_email", e.target.value)} placeholder="ops@example.com" />
          </div>
        )}

        <div className="rounded-lg border border-dashed p-3 space-y-2 bg-muted/30">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm font-medium">
              <Sparkles className="h-4 w-4 text-primary" />
              Compose with Bridgette
            </div>
            <Button type="button" size="sm" variant={aiOpen ? "secondary" : "outline"} onClick={() => setAiOpen((v) => !v)}>
              {aiOpen ? "Hide" : "Open"}
            </Button>
          </div>
          {aiOpen && (
            <div className="space-y-2">
              <Textarea
                rows={2}
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
                placeholder={`e.g. "Let the customer know we're scheduling their ${stageName || "next stage"} this week and ask for their preferred day."`}
              />
              <Button type="button" size="sm" onClick={composeWithBridgette} disabled={aiLoading}>
                {aiLoading ? <><Loader2 className="h-3 w-3 mr-1 animate-spin" />Drafting...</> : "Generate draft"}
              </Button>
            </div>
          )}
        </div>

        <div>
          <Label>Subject</Label>
          <Input value={config.subject || ""} onChange={(e) => set("subject", e.target.value)} placeholder="Update on your project" />
        </div>
        <div>
          <Label>Body</Label>
          <Textarea
            rows={12}
            className="min-h-[260px] font-mono text-sm"
            value={config.body || ""}
            onChange={(e) => set("body", e.target.value)}
            placeholder="Hi {{customer_name}}, your job has moved to the next stage..."
          />
          <p className="text-xs text-muted-foreground mt-1">
            Tokens: {"{{customer_name}}"}, {"{{job_number}}"}, {"{{address}}"}
          </p>
        </div>
      </div>
    );
  }

  if (actionType === "create_task") {
    return (
      <div className="space-y-3">
        <div>
          <Label>Task title</Label>
          <Input value={config.title || ""} onChange={(e) => set("title", e.target.value)} placeholder="Follow up with customer" />
        </div>
        <div>
          <Label>Description</Label>
          <Textarea rows={3} value={config.description || ""} onChange={(e) => set("description", e.target.value)} />
        </div>
        <div>
          <Label>Assign to</Label>
          <Select value={config.assignee_field || "sales_rep"} onValueChange={(v) => set("assignee_field", v)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="sales_rep">Assigned sales rep</SelectItem>
              <SelectItem value="creator">Job creator</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Due in (days)</Label>
          <Input
            type="number"
            min={0}
            value={config.due_in_days ?? 1}
            onChange={(e) => set("due_in_days", Number(e.target.value))}
          />
        </div>
      </div>
    );
  }

  if (actionType === "send_notification") {
    return (
      <div className="space-y-3">
        <div>
          <Label>Notify</Label>
          <Select value={config.recipient_field || "sales_rep"} onValueChange={(v) => set("recipient_field", v)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="sales_rep">Assigned sales rep</SelectItem>
              <SelectItem value="org_admins">All org admins</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Message</Label>
          <Textarea
            rows={3}
            value={config.message || ""}
            onChange={(e) => set("message", e.target.value)}
            placeholder="Job {{job_number}} just moved to this stage."
          />
        </div>
      </div>
    );
  }

  // webhook
  return (
    <div className="space-y-3">
      <div>
        <Label>URL</Label>
        <Input value={config.url || ""} onChange={(e) => set("url", e.target.value)} placeholder="https://hooks.slack.com/..." />
      </div>
      <div>
        <Label>Method</Label>
        <Select value={config.method || "POST"} onValueChange={(v) => set("method", v)}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="POST">POST</SelectItem>
            <SelectItem value="PUT">PUT</SelectItem>
            <SelectItem value="GET">GET</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>Payload (JSON, optional)</Label>
        <Textarea
          rows={4}
          value={config.payload_template || ""}
          onChange={(e) => set("payload_template", e.target.value)}
          placeholder={'{"job_id":"{{job_id}}","stage":"{{stage}}"}'}
        />
        <p className="text-xs text-muted-foreground mt-1">
          Leave blank to send the full job object. Tokens: {"{{job_id}}"}, {"{{job_number}}"}, {"{{stage}}"}, {"{{customer_name}}"}.
        </p>
      </div>
    </div>
  );
}
